// apps/web/src/tour/messengerTour.ts
import type { Tour, TourCtx } from '@/tour/tourTypes';
import { useBrainStore } from '@/state/brainStore';

const outbound = (c: TourCtx['customer']) =>
  (c?.conversation ?? []).filter((e) => e.direction === 'out').length;
const depositMsgs = (c: TourCtx['customer']) =>
  (c?.conversation ?? []).filter((e) => Boolean(e.depositId)).length;
const di = (c: TourCtx['customer']) => c?.designIntent;

export const messengerTour: Tour = {
  id: 'messenger',
  endedEarly: (ctx) => {
    const id = ctx.customer?.id;
    if (!id) return false;
    return Boolean(useBrainStore.getState().sequenceState[id]?.sourceConversationId);
  },
  steps: [
    {
      id: 'reply',
      coach: 'Type your reply to the customer',
      anchor: '[data-testid="composer-body"]',
      done: (ctx) => {
        const send = ctx.el('[data-testid="composer-send"]') as HTMLButtonElement | null;
        return (!!send && !send.disabled) || outbound(ctx.customer) > ctx.baseline.outbound;
      },
    },
    {
      id: 'send',
      coach: 'Send your reply',
      anchor: '[data-testid="composer-send"]',
      done: (ctx) => outbound(ctx.customer) > ctx.baseline.outbound,
    },
    {
      id: 'sessions',
      coach: 'How many sittings — one-off or multiple?',
      anchor: '[data-testid="messenger-brief-piece-mode"]',
      autoScroll: true,
      done: (ctx) => di(ctx.customer)?.pieceMode != null,
    },
    {
      id: 'piece',
      coach: 'Name the piece',
      anchor: '[data-testid="messenger-brief-piece-name"]',
      autoScroll: true,
      done: (ctx) => Boolean(di(ctx.customer)?.newPieceTitle?.trim()),
    },
    {
      id: 'style',
      coach: 'Pick a style',
      anchor: '[data-testid="messenger-brief-style-row"]',
      autoScroll: true,
      done: (ctx) => Boolean(ctx.customer?.preferredStyle),
    },
    {
      id: 'artist',
      coach: 'Choose an artist',
      anchor: '[data-testid="messenger-brief-pref-artist"]',
      autoScroll: true,
      done: (ctx) => Boolean(ctx.customer?.preferredArtistId),
    },
    {
      id: 'placement',
      coach: 'Set the placement',
      anchor: '[data-testid="messenger-brief-placement-look"]',
      autoScroll: true,
      done: (ctx) => Boolean(di(ctx.customer)?.bodyPart || di(ctx.customer)?.placement),
    },
    {
      id: 'colour',
      coach: 'Black & grey or colour?',
      anchor: '[data-testid="messenger-brief-colour-row"]',
      autoScroll: true,
      done: (ctx) => Boolean(di(ctx.customer)?.colour),
    },
    {
      id: 'size',
      coach: 'Pick a size — this sets the deposit',
      anchor: '[data-testid="messenger-brief-size-row"]',
      autoScroll: true,
      done: (ctx) => Boolean(di(ctx.customer)?.size),
    },
    {
      id: 'forward',
      coach: 'Book it now, or push to the agent queue',
      anchor: '[data-testid="messenger-brief-check-availability"]',
      secondaryAnchor: '[data-testid="composer-push"]',
      done: (ctx) => ctx.el('[data-testid="slot-deposit-pane"]') != null,
    },
    {
      id: 'day',
      coach: 'Pick a day with an open window',
      anchor: '[data-testid="artist-heatmap"]',
      autoScroll: true,
      done: (ctx) => ctx.el('[data-testid^="free-window"]') != null,
    },
    {
      id: 'window',
      coach: 'Choose a time — pick a free window',
      anchor: '[data-testid="free-windows"]',
      autoScroll: true,
      done: (ctx) => ctx.el('[data-testid="pane-pinned-slot"]') != null,
    },
    {
      id: 'senddeposit',
      coach: 'Send the deposit for this slot',
      anchor: '[data-testid="pane-send-deposit"]',
      autoScroll: true,
      done: (ctx) => ctx.el('[data-testid="deposit-modal-send"]') != null,
    },
    {
      id: 'sendlink',
      coach: 'Send the deposit link',
      anchor: '[data-testid="deposit-modal-send"]',
      done: (ctx) => depositMsgs(ctx.customer) > ctx.baseline.depositMsgs,
    },
  ],
};

export const TOURS: Record<string, Tour> = { messenger: messengerTour };
